/* eslint-disable no-console */

import { removeCasesFromCache } from '../cache/cases/react-query-job-cache';
import { userType, caseType } from '../models/marleyTypes';
import {
  ADD_JOB,
  API_ERROR,
  SET_CASE_SELECTOR,
  ACTIVE_JOB,
  SET_ACTIVE_CHAT,
  CLEAN_SEND_SURVEY,
  WELCOME_EMAIL_REQUEST_CLEAN,
  SET_EDIT_MODE,
  WELCOME_EMAIL_IS_SENT,
  RESEND_WELCOME_EMAIL_FOR_JOB_ERROR,
  CHAT_SEND_FILE_STARTED,
  CHAT_SEND_FILE_COMPLETED,
  UPDATE_JOB,
  INLINE_UPDATE_JOB_QH_DISMISSALS,
  SNACKBAR_SHOW,
  SHOW_SEND_FILE_MODAL,
  PUT_JOB,
  UPDATE_JOB_CALLBACK,
  BULK_UPDATE_JOB,
  READ_NOTE_MENTION,
  ADD_REPLY,
  DELETE_REPLY,
  PATCH_JOB,
  UPDATE_JOB_SEARCH,
  UPDATE_JOB_IDS_INBOX_FILTER,
  SET_EXPAND_SEARCH_BAR,
  UPDATE_SEARCH_RESULTS_COUNT,
  UPDATE_TOTAL_CHAT_COUNT,
  REDACT_IMAGE,
  VIEW_REDACTED_IMAGE,
  EXIT_ACTIVE_WORKFLOW,
  UPDATE_UNREAD_MESSAGES,
  UPDATE_LAST_VIEWED_MESSAGE,
  SET_ACTIVE_CHAT_MEDIA,
} from '../constants/actions';
import {
  SEND_SURVEY_PROXY,
  CREATE_JOB,
  CREATE_NOTE,
  GET_NOTES,
  DELETE_NOTE,
  GET_CHAT_MEDIA,
  WELCOME_MAIL,
  GET_JOB,
  SET_JOB_LANGUAGE,
  GET_SHORTENED_PUBLIC_URL_FOR_GET,
  GET_SHORT_CODE_MEDIA,
  SEND_MESSAGE_TO_CHAT,
  RETRY_MESSAGE,
  CREATION_SOURCE,
  PUT_JOB_ENDPOINT,
  RESEND_WELCOME_EMAIL,
  CREATE_CASE,
  GET_CHAT_MESSAGES_PAGE_V2,
  BULK_UPDATE_CASES_ENDPOINT,
  READ_NOTE_MENTION_ENDPOINT,
  ADD_REPLY_ENDPOINT,
  DELETE_REPLY_ENDPOINT,
  PATCH_JOB_ENDPOINT,
  REDACT_IMAGE_ENDPOINT,
  PATCH_LAST_VIEWED_MESSAGE,
  PATCH_MARK_CHAT_UNREAD,
  PATCH_MARK_MESSAGE_UNREAD,
  PUT_ACTIVE_WORKFLOW,
  BULK_PATCH_CASES_ENDPOINT,
} from '../constants/endpoints';

import * as apiErrors from '../constants/apierrors';
import Chat from '../models/chat';
import Job from '../models/job';
import Message from '../models/message';
import { apiRequest, dispatchRequest } from './api/api';
import { apiCall, doRequest, validateAuthErrorAndDispatchDeauth } from './api';
import { closeForm } from './ui';
import { getScheduledMessages } from './chat';
import { uploadMedia } from './media';
import i18n from '@app/i18n';
import handleChatMessageItem, {
  handleChatMessageRetry,
} from '@app/cache/chat-messages/chat-cache';
import { getCreationSource } from '@app/helpers/creation-source';
import { isJobPrivacy, isVisibleToOperator } from '@app/reducers/helpers';
import { QUERY_KEYS } from '@app/constants/service';
import { isFeatureEnabledFromCache } from '@app/services/organization-features';
import { s3toPipedUrl } from '@app/helpers/urls';
import {
  getContactTimeZone,
  isCentralizedQuietHoursFeatureFlagOn,
  isCurrentlyQuietHours,
  resolveQuietHoursContextFromStore,
} from '@app/helpers/quiet-hours';
import { CENTRALIZED_QUIET_HOURS } from '@app/constants/feature-definitions';
import { randomUUID } from '@app/utils/uuid';
import {
  DATE_TIME_SHORT_MONTH_DAY_12H,
  formatDateTime,
} from '@app/i18n/formatters/date';

const MESSAGE_REQUEST_LIMIT = 32;
export const UP = 'UP';
export const DOWN = 'DOWN';

export const cleanSendSurvey = () => ({ type: CLEAN_SEND_SURVEY });
export const cleanWelcomeEmailRequest = () => ({
  type: WELCOME_EMAIL_REQUEST_CLEAN,
});

export const assignUser = (data) => updateJob(data);

export const setCaseLanguage = ({ caseId, languagePreference }) => {
  const creationSource = getCreationSource();
  const args = {
    endpoint: SET_JOB_LANGUAGE(caseId),
    method: 'PUT',
    action: 'MODIFIED_JOB_LANGUAGE',
    body: { languagePreference, creationSource },
    process: ({ body }) => body,
  };
  return apiCall({ args });
};

// export const openJob = id => ({ type: OPEN_JOB, payload: id })

export const setCaseSelector = (selectorId) => ({
  type: SET_CASE_SELECTOR,
  payload: selectorId,
});

export const loadLastMessagePage = (getStore) => {
  const store = getStore();
  const { _id: organizationId } = store.auth?.user?.ownerOrganization || {};
  const { id: chatId } = store?.jobs?.activeChat || {};
  if (!chatId) {
    console.warn('tried to load messages with no active chat');
    return [];
  }
  return doRequest({
    endpoint: GET_CHAT_MESSAGES_PAGE_V2(
      organizationId,
      chatId,
      '',
      UP,
      MESSAGE_REQUEST_LIMIT,
    ),
  }).then((chatResponse) =>
    (chatResponse.chat ?? []).map((message) => new Message(message)),
  );
};

export const sendSurvey = (job) => {
  const { referenceId, _id: jobId, type = 'case' } = job;
  const args = {
    endpoint: SEND_SURVEY_PROXY,
    method: 'POST',
    body: {
      jobId,
    },
    action: 'SEND_SURVEY',
    process: ({ body }) => body,
    callback: ({ body, dispatch }) => {
      const values = { type: type.toLowerCase(), referenceId };
      const text = body?.deferred
        ? i18n.t('surveyScheduled', values)
        : i18n.t('surveySent', values);
      dispatch({
        type: SNACKBAR_SHOW,
        payload: { text },
      });
    },
    errorCallback: ({ dispatch, error }) => {
      dispatch({
        type: SNACKBAR_SHOW,
        payload: { isError: true, text: error?.message },
      });
    },
  };

  return apiCall({ args });
};

export function newJobCreated(job, userId) {
  const payload = {
    job: new Job(job, userId),
    operatorId: userId,
  };
  return { type: ADD_JOB, payload };
}

export function createJob(customerId) {
  return (dispatch, getStore) => {
    const opts = {
      endpoint: CREATE_JOB,
      method: 'POST',
      body: { customerId },
    };
    return doRequest(opts)
      .then((body) => {
        const userId = getStore().auth.user._id;
        dispatch(newJobCreated(body, userId));
        // todo: avoid extra requests for new job creation
        // eslint-disable-next-line no-undef
        dispatch(openJob(body._id));
      })
      .catch((error) => {
        validateAuthErrorAndDispatchDeauth(error, dispatch);
        dispatch({ type: API_ERROR, payload: apiErrors.JOB_CREATE_ERROR });
      });
  };
}

export function addCase(type, template) {
  return { type: `NEW_${type}`, payload: new Job(template) };
}

export function createCase(type, data) {
  const addSource = ['POLICY', 'CLAIM', 'CASE'];
  const alteredData = { ...data };
  if (!alteredData.metaData) {
    alteredData.metaData = {};
  }
  delete alteredData.metadataReferenceId;
  delete alteredData.type;
  delete alteredData.endUserNumber;

  if (addSource.indexOf(type) > -1) {
    alteredData.creationSource = getCreationSource();
  }
  const args = {
    endpoint: CREATE_CASE,
    method: 'POST',
    body: alteredData,
    action: `ADD_${type}`,
    callback: ({ body, dispatch }) => {
      dispatch(closeForm(userType));
      dispatch(closeForm(caseType));
      return addCase(type, body);
    },
  };
  return apiCall({ args });
}

// used in the pollbox middleware to handle jobs live update
export function updateJob(data) {
  return (dispatch, getStore) => {
    const operatorId = getStore().auth.user._id;
    const { permissions } = getStore().auth;
    const job = new Job(data);
    return dispatch({
      type: UPDATE_JOB,
      payload: { job, operatorId, permissions },
    }); // remove on final
  };
}

// Surgical QH-dismissals update — sidesteps the UPDATE_JOB list-resort flicker.
export const updateJobQuietHoursDismissals = ({
  jobId,
  quietHoursWarningDismissals,
}) => ({
  type: INLINE_UPDATE_JOB_QH_DISMISSALS,
  payload: { jobId, quietHoursWarningDismissals },
});

export const patchJob = (id, update) =>
  apiCall({
    args: {
      endpoint: PATCH_JOB_ENDPOINT(id),
      method: 'PATCH',
      action: PATCH_JOB,
      body: update,
    },
  });

export const buildCaseUpdateBody = (job) => ({
  marleyNumber: job.marleyNumber,
  marleyNumberStatus: job.marleyNumberStatus,
  metaData: job.metaData,
  caseTypeId: job.caseTypeId,
  metadataReferenceId: job.metadataReferenceId,
  reference: job?.metaData?.[job.metadataReferenceId],
  referenceId: job?.metaData?.[job.metadataReferenceId],
  operatorId: job?.operatorId?.id ?? job.operatorId,
  customerId: job?.customerId?.id ?? job.customerId,
  inTranslationMode: job.inTranslationMode,
  languagePreference: job.languagePreference,
  isActive: job.isActive,
  isOpen: job.isOpen,
  secondaryOperatorIds: job.secondaryOperatorIds,
  type: job.type,
  branding: job.branding,
  privacy: job?.privacy ?? 'public',
  needsAttentionMessageCount: job?.needsAttentionMessageCount ?? 0,
  isTotalLoss: job.isTotalLoss,
  releaseIssues: job.releaseIssues,
  lineOfBusiness: job.lineOfBusiness,
  queueId: job.queueId,
  lastEventCreationSource: getCreationSource(),
});

export const buildCasePatchBody = (job) => ({
  ...job,
  lastEventCreationSource: getCreationSource(),
});

export function reOpenJob(job, toaster, toastIdRef) {
  const jobId = job.id;
  const body = buildCaseUpdateBody({ ...job, isOpen: true });
  const args = {
    endpoint: PUT_JOB_ENDPOINT(jobId),
    method: 'PUT',
    action: PUT_JOB,
    body,
    callback: ({ body, dispatch }) => {
      dispatch(closeForm(userType));
      dispatch(closeForm(caseType));
      dispatch(updateActiveJob(body._id));
      if (toastIdRef.current) {
        toaster.update(toastIdRef.current, {
          title: 'Successfully re-opened case.',
          type: 'success',
          duration: 5000,
        });
      }
      return { payload: new Job(body), type: UPDATE_JOB_CALLBACK };
    },
    errorCallback: () => {
      if (toastIdRef.current) {
        toaster.update(toastIdRef.current, {
          title: 'Could not re-open case.',
          description: 'Please try again later.',
          type: 'error',
          duration: 5000,
        });
      }
    },
  };
  return apiCall({ args });
}

// used to start update (PUT) job request
export function jobPutRequest(
  job,
  isReopeningJob = null,
  shouldUpdateActiveJob = true,
  toaster = null,
) {
  const jobId = job.id;
  const body = buildCaseUpdateBody(job);
  const toasterIdRef = toaster
    ? toaster.create({
        title: 'Updating case...',
        type: 'loading',
      })
    : null;
  const args = {
    endpoint: PUT_JOB_ENDPOINT(jobId),
    method: 'PUT',
    action: PUT_JOB,
    body,
    callback: ({ body, dispatch }) => {
      dispatch(closeForm(userType));
      dispatch(closeForm(caseType));

      if (shouldUpdateActiveJob) {
        dispatch(updateActiveJob(body._id));
      }
      const successMessage =
        typeof isReopeningJob === 'boolean'
          ? `Successfully ${isReopeningJob ? 're-opened' : 'closed'} case.`
          : 'Successfully updated case.';
      if (toaster) {
        toaster.update(toasterIdRef, {
          title: successMessage,
          type: 'success',
          duration: 5000,
        });
      } else {
        dispatch({
          type: SNACKBAR_SHOW,
          payload: { isError: false, text: successMessage },
        });
      }

      return { payload: new Job(body), type: UPDATE_JOB_CALLBACK };
    },
    errorCallback: ({ dispatch, error }) => {
      const errorMessage =
        typeof isReopeningJob === 'boolean'
          ? `Could not ${isReopeningJob ? 're-open' : 'close'} case. Please try again.${isReopeningJob ? ' You may need to close an existing job for this end user first.' : ''}`
          : 'Could not update case. Please try again later. ';

      const serverMessage =
        typeof error === 'string'
          ? error
          : (error?.message ?? error?.error ?? '');

      if (toaster) {
        toaster.update(toasterIdRef, {
          title: errorMessage,
          type: 'error',
          description: serverMessage || undefined,
          duration: 5000,
        });
      } else {
        const snackbarText = serverMessage
          ? `${errorMessage} ${serverMessage}`
          : errorMessage;
        dispatch({
          type: SNACKBAR_SHOW,
          payload: { isError: true, text: snackbarText },
        });
      }
    },
  };
  return apiCall({ args });
}

export function closeJob(job, toaster, toastIdRef) {
  const jobId = job._id;
  const body = buildCasePatchBody(job);
  const args = {
    endpoint: PATCH_JOB_ENDPOINT(jobId),
    method: 'PATCH',
    action: PUT_JOB,
    body,
    callback: ({ body, dispatch }) => {
      dispatch(closeForm(userType));
      dispatch(closeForm(caseType));
      if (toastIdRef.current) {
        toaster.update(toastIdRef.current, {
          title: 'Successfully closed case.',
          type: 'success',
          duration: 5000,
        });
      }
      return { payload: new Job(body), type: UPDATE_JOB_CALLBACK };
    },
    errorCallback: () => {
      if (toastIdRef.current) {
        toaster.update(toastIdRef.current, {
          title: 'Could not close case.',
          description: 'Please try again later.',
          type: 'error',
          duration: 5000,
        });
      }
    },
  };
  return apiCall({ args });
}

export function jobBulkUpdateRequest(jobs, action, toaster) {
  const body = jobs.map((job) => {
    const caseUpdateBody = buildCaseUpdateBody(job);
    caseUpdateBody._id = job._id;
    return caseUpdateBody;
  });
  let toastIdRef;
  const args = {
    endpoint: BULK_UPDATE_CASES_ENDPOINT,
    method: 'PUT', // may be updated to PATCH on version 2.0
    action: BULK_UPDATE_JOB,
    body,
    process: () => ({ body }),
    preApiCallback: () => {
      toastIdRef = toaster.create({
        title: action.loadingTitle,
        type: 'loading',
      });
    },
    callback: ({ body: callbackBody }) => {
      if (toastIdRef) {
        toaster.update(toastIdRef, {
          title: action.successTitle,
          type: 'success',
          duration: 5000,
        });
      }
      return { payload: callbackBody, type: 'UPDATE_JOB_CALLBACK' };
    },
    errorCallback: ({ error }) => {
      if (toastIdRef) {
        toaster.update(toastIdRef, {
          title: action.errorTitle,
          description: error?.message,
          type: 'error',
          duration: 5000,
        });
      }
    },
  };
  return apiCall({ args });
}

const updateActiveJob = (jobId) => async (dispatch, getStore) => {
  const store = getStore();
  const activeJob = store.jobs?.activeJob ?? {};

  if (activeJob._id === jobId) {
    dispatch(setActiveJob(jobId));
  }
};

export function bulkCloseJobs(jobs, toaster, toastIdRef, queryClient, titles) {
  const jobIds = jobs.map((j) => j.jobId);
  const args = {
    endpoint: BULK_PATCH_CASES_ENDPOINT,
    method: 'PATCH',
    action: BULK_UPDATE_JOB,
    body: { jobs },
    callback: ({ body, dispatch }) => {
      dispatch(closeForm(userType));
      dispatch(closeForm(caseType));
      dispatch(updateActiveJob(body._id));
      if (queryClient) {
        removeCasesFromCache(queryClient, jobIds);
      }
      if (toastIdRef.current) {
        toaster.update(toastIdRef.current, {
          title: titles.successTitle,
          type: 'success',
          duration: 5000,
        });
      }
      return { payload: new Job(body), type: UPDATE_JOB_CALLBACK };
    },
    errorCallback: () => {
      if (toastIdRef.current) {
        toaster.update(toastIdRef.current, {
          title: titles.errorTitle,
          type: 'error',
          duration: 5000,
        });
      }
    },
  };
  return apiCall({ args });
}

export const processJobs = (jobs, userId = false) =>
  jobs.map((job) => new Job(job, userId));

export const clearActiveJob = (caseId) => ({
  type: 'CLEAR_ACTIVE_JOB',
  payload: caseId,
});

export const setActiveChat = (job) => async (dispatch, getStore) => {
  try {
    const { primaryChatId: chatId, _id: jobId } = job;

    if (!chatId) {
      console.error(
        `Error: primaryChatId is missing in the job object for jobId: ${jobId || 'unknown'}.`,
      );
      dispatch({
        type: API_ERROR,
        payload: 'Missing primaryChatId in job object',
      });
      return;
    }
    const store = getStore();
    const authId = store?.auth?.user?._id;
    const activeJob = store?.jobs?.activeJob;
    // todo: revisit this logic

    const recipientId =
      job?.customer?.id === authId ? job?.operatorIds?.[0] : job?.customer?.id;

    let lastViewedMessageId = activeJob?.lastViewedMessage?.messageId;

    if (!lastViewedMessageId && activeJob?.usersLastViewedMessage && authId) {
      lastViewedMessageId =
        activeJob?.usersLastViewedMessage[authId]?.messageId;
    }

    const chat = new Chat({
      _id: chatId,
      jobId,
      senderId: authId,
      recipientId,
      messages: [],
      media: [],
    });

    const type = SET_ACTIVE_CHAT;
    const { _id: operatorId } = getStore().auth.user;
    const { permissions } = getStore().auth;
    dispatch({ type, payload: { chat, operatorId, permissions } });
  } catch (error) {
    console.error(error);
    validateAuthErrorAndDispatchDeauth(error, dispatch);
    dispatch({ type: API_ERROR, payload: 'Setting Active Chat Error' });
  }
};

export const setActiveChatMedia = (chatId) => async (dispatch) => {
  try {
    const chatMedia = await doRequest({ endpoint: GET_CHAT_MEDIA(chatId) });
    const media =
      chatMedia?.map((mediaItem) => ({
        ...mediaItem,
        fileUrl: s3toPipedUrl(mediaItem?.fileUrl),
      })) || [];
    dispatch({ type: SET_ACTIVE_CHAT_MEDIA, payload: { chat: { media } } });
  } catch (error) {
    console.error(`Failed to fetch chat media for chatId ${chatId}:`, error);
    dispatch({ type: API_ERROR, payload: 'Failed to fetch chat media' });
  }
};

export const setActiveJob = (jobId) => async (dispatch, getStore) => {
  try {
    const activeJobArgs = {
      endpoint: GET_JOB(jobId, getCreationSource()),
      process: ({ body }) => {
        const authId = getStore().auth?.user?._id;
        const permissions = getStore().auth?.permissions || [];
        const { job } = body || {};
        const hasAccessToJob =
          isJobPrivacy(job, 'public') ||
          (isJobPrivacy(job, 'private-redacted') &&
            isVisibleToOperator(job, authId, permissions));
        // ultimately this logic should move to the backend - then the route would return
        // a 403 FORBIDDEN response that would result in the same update to the store
        if (!hasAccessToJob) {
          throw new Error('Forbidden');
        }
        dispatch({ type: ACTIVE_JOB, payload: new Job(job) });
        dispatch(setActiveChat(job));
      },
    };

    dispatch(apiCall({ args: activeJobArgs }));
  } catch (error) {
    validateAuthErrorAndDispatchDeauth(error, dispatch);
    dispatch({ type: API_ERROR, payload: apiErrors.JOB_GET_ERROR });
  }
};

export function sendWelcomeMail(id, type) {
  const body = { origin: 'web-app' };
  body[`${type}Id`] = id;
  const args = {
    endpoint: RESEND_WELCOME_EMAIL(type),
    method: 'POST',
    process: (t) => t,
    body,
    action: 'SEND_WELCOME_EMAIL',
  };
  return apiCall({ args });
}

export function setEditMode() {
  return { type: SET_EDIT_MODE };
}

export function resendWelcomeMail(data) {
  return (dispatch) => {
    const opts = {
      endpoint: WELCOME_MAIL,
      method: 'POST',
      body: data,
    };
    return doRequest(opts)
      .then(() => {
        dispatch({ type: WELCOME_EMAIL_IS_SENT });
      })
      .catch((error) => {
        validateAuthErrorAndDispatchDeauth(error, dispatch);
        dispatch({
          type: RESEND_WELCOME_EMAIL_FOR_JOB_ERROR,
          payload: apiErrors.SEND_WELCOME_MESSAGE_ERROR,
        });
      });
  };
}

export const getNotes = (caseId) => {
  const args = {
    endpoint: GET_NOTES(caseId),
    method: 'GET',
    action: 'LOAD_NOTES',
    process: ({ body }) => body,
  };

  return apiCall({ args });
};

export const readNoteMention = (caseId, userId, noteId, replyId = null) => {
  const body = {
    userId,
    replyId,
  };
  const args = {
    endpoint: READ_NOTE_MENTION_ENDPOINT(caseId, noteId),
    method: 'DELETE',
    action: READ_NOTE_MENTION,
    body,
    process: () => ({ userId, noteId, replyId }),
  };

  return apiCall({ args });
};

export const deleteNote = (ids) => {
  const { chatId } = ids;
  const body = {
    chatId,
  };
  const args = {
    endpoint: DELETE_NOTE(ids),
    method: 'PUT',
    action: 'DELETE_NOTE',
    body,
    process: ({ body }) => body,
  };

  return apiCall({ args });
};

export const createNote = ({
  caseId,
  noteText,
  unreadNoteMentions,
  plainText,
}) => {
  const body = {
    caseId,
    noteText,
    creationSource: CREATION_SOURCE,
    unreadNoteMentions,
    plainText,
  };

  const args = {
    endpoint: CREATE_NOTE(caseId),
    method: 'POST',
    action: 'CREATE_NOTE',
    body,
    process: ({ body }) => body,
  };

  return apiCall({ args });
};

export const addReply = ({
  caseId,
  noteId,
  replyText,
  unreadNoteMentions,
  plainText,
}) => {
  const body = {
    caseId,
    noteId,
    replyText,
    unreadNoteMentions,
    plainText,
  };

  const args = {
    endpoint: ADD_REPLY_ENDPOINT(caseId, noteId),
    method: 'POST',
    action: ADD_REPLY,
    body,
    process: ({ body }) => body,
  };

  return apiCall({ args });
};

export const deleteReply = (ids) => {
  const { chatId } = ids;
  const body = {
    chatId,
  };
  const args = {
    endpoint: DELETE_REPLY_ENDPOINT(ids),
    method: 'PUT',
    action: DELETE_REPLY,
    body,
    process: ({ body }) => body,
  };

  return apiCall({ args });
};

export const queueMessage = (args) =>
  dispatchRequest(apiRequest.create.message.queue(args), {
    type: 'CREATE_SCHEDULED_MESSAGE',
    successCallback: () => (dispatch) => {
      dispatch(getScheduledMessages(args.chatId));
      const eventDate =
        args.eventTime !== null &&
        args.eventTime !== undefined &&
        args.eventTime !== ''
          ? new Date(args.eventTime)
          : null;
      const scheduledLabel =
        eventDate && !Number.isNaN(eventDate.getTime())
          ? formatDateTime(
              eventDate,
              DATE_TIME_SHORT_MONTH_DAY_12H,
            ).toUpperCase()
          : '';
      dispatch({
        type: 'SNACKBAR_SHOW',
        payload: {
          text: scheduledLabel
            ? `Message scheduled for ${scheduledLabel}`
            : 'Message scheduled',
        },
      });

      // Clear hasUnaddressedMessage flag when scheduling a message
      if (args.jobId) {
        const apiArgs = {
          endpoint: PATCH_JOB_ENDPOINT(args.jobId),
          method: 'PATCH',
          body: { hasUnaddressedMessage: false },
        };
        apiCall({ args: apiArgs })(dispatch);
      }
    },
  });

export const sendMessage =
  ({
    chatId,
    jobId,
    message,
    queryClient,
    isRetryAttempt = false,
    originalMessageId = '',
    sendImmediately = false,
  }) =>
  async (dispatch, getStore) => {
    const data = {
      chatId,
      jobId,
      messageBody: message,
      messageId: randomUUID(),
      creationSource: getCreationSource(),
    };
    if (sendImmediately) {
      data.sendImmediately = true;
    }
    if (originalMessageId) {
      data.originalMessageId = originalMessageId;
    }
    const thisActiveChat = getStore().jobs.activeChat;
    const thisActiveJob = getStore().jobs.activeJob;
    const customer = thisActiveJob?.customer ?? {};
    const userLanguagePreference = customer?.languagePreference ?? 'en';
    const userInTranslationMode = customer?.inTranslationMode ?? false;
    const jobLanguagePreference = thisActiveJob?.languagePreference ?? 'en';
    const jobInTranslationMode = thisActiveJob?.inTranslationMode ?? false;
    const languagePreference =
      jobInTranslationMode && jobLanguagePreference !== null
        ? jobLanguagePreference
        : userLanguagePreference;
    const inTranslationMode =
      (jobInTranslationMode || userInTranslationMode) &&
      languagePreference !== 'en';

    const opts = {
      endpoint: isRetryAttempt
        ? RETRY_MESSAGE(chatId)
        : SEND_MESSAGE_TO_CHAT(chatId),
      method: 'POST',
      body: data,
    };
    data.isInbound = false;
    data.createdAt = new Date().toISOString();
    data._id = data.messageId;
    data.body = data.messageBody;

    data.author = getStore().auth.user;
    if (inTranslationMode) {
      data.additionalInfo = {
        translatedMessage: 'Translating...',
      };
    }
    const authorPrefix =
      thisActiveJob?.authorPrefix ||
      (data?.author?.profile?.lastName
        ? `${data?.author?.profile?.firstName} ${data?.author?.profile?.lastName[0]}.`
        : 'Marley');
    const dataWithPrefix = { ...data, bodyPrefix: authorPrefix };

    // Detect when the server is going to defer this message rather than
    // dispatch it now: the MANUAL_CHAT_MESSAGE policy respects quiet hours,
    // so unless the caller passes sendImmediately we'll get back a synthetic
    // deferred message with no live-update follow-up. Skip the optimistic
    // chat-log write in that case — the deferred message belongs in the
    // quiet-hours drawer, not the message list. Fixes HMB-17064 / HMB-17431.
    const state = getStore();
    const isCentralizedQuietHoursEnabled =
      isCentralizedQuietHoursFeatureFlagOn() &&
      isFeatureEnabledFromCache(queryClient, CENTRALIZED_QUIET_HOURS, {
        failClosed: true,
      });
    const shouldEvaluateQuietHours =
      isCentralizedQuietHoursEnabled && !sendImmediately;

    let quietHours;
    let evaluationTimeZone = null;
    if (shouldEvaluateQuietHours) {
      ({ config: quietHours, evaluationTimeZone } =
        resolveQuietHoursContextFromStore({
          state,
          queryClient,
          contactTimeZone: getContactTimeZone(thisActiveJob),
        }));
    }

    const willDeferForQuietHours =
      shouldEvaluateQuietHours &&
      isCurrentlyQuietHours(quietHours, evaluationTimeZone, new Date());

    if (!willDeferForQuietHours) {
      handleChatMessageItem(
        thisActiveChat,
        new Message(dataWithPrefix),
        '',
        queryClient,
        getStore(),
      );
    }
    if (data.originalMessageId) {
      // Retries during QH deferral still skip the optimistic bubble, but the
      // failed original must show retryAttempted styling in the chat log.
      handleChatMessageRetry(
        thisActiveChat,
        data.originalMessageId,
        queryClient,
        getStore(),
      );
    }

    await doRequest(opts);
    if (willDeferForQuietHours && queryClient) {
      // The server created a QUIET_HOURS_DEFERRED_SEND task. Refresh the
      // task list so the drawer shows the new pending message.
      queryClient.invalidateQueries({
        queryKey: QUERY_KEYS.QUIET_HOURS_TASKS(jobId),
      });
    }
  };

export const VALID_MESSAGING_FILE_TYPES = [
  'image/png',
  'image/jpeg',
  'image/gif',
  'video/mp4',
  'audio/mp4',
  'text/vcard',
  'application/pdf',
  'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet', // .xlsx
  'application/vnd.openxmlformats-officedocument.wordprocessingml.document', // .docx
];

export const MESSAGING_FILE_MEGABYTE_LIMIT = 25;

// Wraps uploadMedia and deals with getting the link to provide in an outbound media message
export const uploadFile = (file, toaster, toastIdRef) => async (dispatch) => {
  try {
    const { key, url } =
      (await uploadMedia(
        file,
        VALID_MESSAGING_FILE_TYPES,
        MESSAGING_FILE_MEGABYTE_LIMIT,
        true,
      )) || {};
    if (!key) {
      throw new Error('No object key to build upload link with');
    }
    return { key, url };
  } catch (e) {
    const error = {
      title: 'File Not Allowed',
      description:
        'For more information on the files we support, please visit the help doc: https://himarley.zendesk.com/hc/en-us/articles/360050427254-Outbound-Media',
      type: 'error',
      duration: 5000,
    };
    if (e?.message === 'EntityTooLarge') {
      error.title = 'File Too Large';
      error.description = `File must be under ${MESSAGING_FILE_MEGABYTE_LIMIT} MB.`;
    } else if (e?.message === 'Invalid file type') {
      error.title = 'Format Not Allowed';
      error.description =
        'Files must be JPEG/JPG, PNG, GIF, MP4, VCARD, PDF, DOCX, or XLSX formats.';
    }
    if (toastIdRef?.current) {
      toaster.update(toastIdRef.current, error);
    }
    dispatch({ type: CHAT_SEND_FILE_COMPLETED });
    console?.error(e);
  }
};

export const setChatSendFileCompleted = () => (dispatch) => {
  dispatch({ type: CHAT_SEND_FILE_COMPLETED });
};

export const sendFile =
  (key, url, file, toaster, toastIdRef) => async (dispatch) => {
    dispatch({ type: CHAT_SEND_FILE_STARTED });
    try {
      if (!key) {
        throw new Error('No object key to build upload link with');
      }
      // get short code for upload to send to customer in message
      const shortenedGetUrl = await doRequest({
        endpoint: GET_SHORTENED_PUBLIC_URL_FOR_GET(url, key),
        method: 'GET',
      });
      if (shortenedGetUrl?.virusScanStatus === 'VIRUS_SCAN_QUEUED') {
        return null;
      }
      // construct final link and put in message
      if (!shortenedGetUrl?.shortCode)
        throw new Error('No shortcode on response');
      const finalLink = `${window.location.origin}/${GET_SHORT_CODE_MEDIA(shortenedGetUrl.shortCode)}`;
      dispatch({
        type: CHAT_SEND_FILE_COMPLETED,
        payload: `Link to file ${file.name}: ${finalLink}`,
      });

      if (toastIdRef?.current) {
        toaster.update(toastIdRef.current, {
          description: 'Upload 100% · File Ready to Send',
          type: 'success',
          duration: 5000,
          meta: {
            closable: true,
          },
        });
      }
    } catch (e) {
      console?.error(e);

      if (e?.virusScanStatus === 'VIRUS_SCAN_INFECTED') {
        if (toastIdRef?.current) {
          toaster.update(toastIdRef.current, {
            title: 'File Blocked',
            description: 'Our system detected a virus and blocked the file.',
            type: 'error',
            duration: 5000,
            meta: {
              closable: true,
            },
          });
        }
      } else if (toastIdRef?.current) {
        toaster.update(toastIdRef.current, {
          title: 'File Send Failed',
          description: 'Failed to send file. Please try again.',
          type: 'error',
          duration: 5000,
          meta: {
            closable: true,
          },
        });
      }
      dispatch({ type: CHAT_SEND_FILE_COMPLETED });
    }
  };

export const showSendFileModal = (show, file) => ({
  type: SHOW_SEND_FILE_MODAL,
  payload: {
    show,
    file,
  },
});

export const updateSearch =
  ({ search, searchDisplayValue }) =>
  (dispatch) => {
    dispatch({
      type: UPDATE_JOB_SEARCH,
      payload: {
        search,
        searchDisplayValue,
      },
    });
  };

export const updateJobIdsInboxFilter =
  ({ jobIds }) =>
  (dispatch) => {
    dispatch({
      type: UPDATE_JOB_IDS_INBOX_FILTER,
      payload: {
        jobIds,
      },
    });
  };

export const setExpandInboxSearchBar = (value) => (dispatch) => {
  dispatch({
    type: SET_EXPAND_SEARCH_BAR,
    payload: {
      expandInboxSearchBar: value,
    },
  });
};

export const updateSearchResultsCount = (value) => (dispatch) => {
  dispatch({
    type: UPDATE_SEARCH_RESULTS_COUNT,
    payload: {
      searchResultsCount: value,
    },
  });
};

export const updateTotalChatCount = (value) => (dispatch) => {
  dispatch({
    type: UPDATE_TOTAL_CHAT_COUNT,
    payload: {
      totalChatCount: value,
    },
  });
};

export const redactImage =
  (messageId, key, fileUrl, redactionData, queryClient) =>
  (dispatch, getStore) => {
    const store = getStore();
    const args = {
      endpoint: REDACT_IMAGE_ENDPOINT(key),
      method: 'PUT',
      body: { messageId },
      process: (t) => t,
      action: '',
      callback: () => {
        dispatch({
          type: REDACT_IMAGE,
          payload: { fileUrl, messageId, redactionData },
        });
        handleChatMessageItem(
          store?.jobs?.activeChat,
          { id: messageId, redactionData },
          REDACT_IMAGE,
          queryClient,
          getStore(),
        );
        dispatch({
          type: SNACKBAR_SHOW,
          payload: { text: 'Successfully redacted image.' },
        });
      },
      errorCallback: () => {
        dispatch({
          type: SNACKBAR_SHOW,
          payload: {
            isError: true,
            text: 'Error redacting image. Please try again.',
          },
        });
      },
    };
    return apiCall({ args })(dispatch, getStore);
  };

export const viewRedactedImage =
  (messageId, queryClient) => (dispatch, getStore) => {
    const store = getStore();
    handleChatMessageItem(
      store?.jobs?.activeChat,
      { id: messageId },
      VIEW_REDACTED_IMAGE,
      queryClient,
      getStore(),
    );
  };

export const updateLastViewedMessage =
  (queryClient, jobId, messageId) => (dispatch, getStore) => {
    const { _id: activeJobId } = getStore()?.jobs?.activeJob || {};
    if (activeJobId === jobId) {
      const args = {
        endpoint: PATCH_LAST_VIEWED_MESSAGE(jobId),
        method: 'PATCH',
        action: UPDATE_LAST_VIEWED_MESSAGE,
        body: { messageId, creationSource: getCreationSource() },
        process: (t) => t,
        errorCallback: () => {
          dispatch({
            type: SNACKBAR_SHOW,
            payload: {
              isError: true,
              text: 'Error marking chat read. Please try again.',
            },
          });
        },
      };
      return apiCall({ args })(dispatch, getStore);
    }
    return null;
  };

/**
 * Similar to updateLastViewedMessage, but calculates the message id of the last viewed message when the id
 * for the message to mark as read is provided.
 */
export const markMessageUnread =
  (queryClient, jobId, messageId) => (dispatch, getStore) => {
    const args = {
      endpoint: PATCH_MARK_MESSAGE_UNREAD(jobId),
      method: 'PATCH',
      action: 'MARK_MESSAGE_UNREAD',
      body: { messageId },
      process: (t) => t,
      callback: () => {
        const store = getStore();
        handleChatMessageItem(
          store?.jobs?.activeChat,
          { _id: messageId },
          UPDATE_UNREAD_MESSAGES,
          queryClient,
          store,
        );
      },
      errorCallback: () => {
        dispatch({
          type: SNACKBAR_SHOW,
          payload: {
            isError: true,
            text: 'Mark Unread isn’t supported for automated messages.',
          },
        });
      },
    };
    return apiCall({ args })(dispatch, getStore);
  };

/**
 * Similar to markMessageUnread, but calculates the last applicable message to mark as read on a chat
 * and sets the last viewed message accordingly
 */
export const markChatUnread = (queryClient, jobId) => (dispatch, getStore) => {
  const args = {
    endpoint: PATCH_MARK_CHAT_UNREAD(jobId),
    method: 'PATCH',
    action: 'MARK_LATEST_APPLICABLE_MESSAGE_ON_CHAT_UNREAD',
    process: (t) => t,
    callback: ({ body }) => {
      const store = getStore();
      handleChatMessageItem(
        store?.jobs?.activeChat,
        { _id: body?.lastViewedMessage?.messageId },
        UPDATE_UNREAD_MESSAGES,
        queryClient,
        store,
      );
    },
    errorCallback: () => {
      dispatch({
        type: SNACKBAR_SHOW,
        payload: {
          isError: true,
          text: 'Mark Unread isn’t supported for automated messages.',
        },
      });
    },
  };
  return apiCall({ args })(dispatch, getStore);
};

export const endActiveWorkflow =
  ({ jobId }) =>
  (dispatch, getStore) => {
    const args = {
      endpoint: PUT_ACTIVE_WORKFLOW(jobId),
      method: 'PUT',
      action: EXIT_ACTIVE_WORKFLOW,
      body: {
        activeWorkflow: null,
        exitReason: 'MANUAL_TAKEOVER',
      },
      callback: ({ body }) => {
        const store = getStore();
        const activeCaseId = body?._id;
        const currentActiveJobId = store?.jobs?.activeJob?._id;
        if (activeCaseId === currentActiveJobId) {
          updateJob(body)(dispatch, getStore);
        }
      },
      errorCallback: ({ error }) => {
        validateAuthErrorAndDispatchDeauth(error, dispatch);
      },
    };
    return apiCall({ args })(dispatch, getStore);
  };
